import { useState, useEffect, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import {
  TrendingUp, Clock, Euro, ArrowUpDown, ArrowUp, ArrowDown,
  Building2, X, Loader2, AlertCircle, SlidersHorizontal
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';
import FiscalYearSelector from '../components/FiscalYearSelector';
import ViewTransactionModal from '../components/validation/ViewTransactionModal';
import { Transaction } from '../types/dashboard';
import { formatCurrency } from '../utils/format';
import { dateRangesFilter, getMonthRangeDates, getPeriodRanges } from '../utils/period';

// ── Couleurs par société ──────────────────────────────────────────────────
const COMPANY_COLOR: Record<string, string> = {
  KI:     '#6366f1',
  KA:     '#10b981',
  DRYADE: '#8b5cf6',
};
const FALLBACK_COLORS = ['#6366f1', '#10b981', '#8b5cf6', '#f59e0b', '#ec4899', '#14b8a6'];

const getColor = (name: string, idx: number) =>
  COMPANY_COLOR[name] ?? FALLBACK_COLORS[idx % FALLBACK_COLORS.length];

// ── Custom Tooltip Recharts ───────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-lg px-4 py-3 min-w-[160px]">
      {label && <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">{label}</p>}
      {payload.map((p: any, i: number) => (
        <div key={i} className="flex items-center gap-2 text-sm">
          <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: p.fill ?? p.color }} />
          <span className="text-slate-500">{p.name}</span>
          <span className="font-semibold text-slate-800 ml-auto pl-3">{formatCurrency(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

const PieTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const d = payload[0];
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-lg px-4 py-3">
      <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">{d.name}</p>
      <p className="text-sm font-semibold text-slate-800">{formatCurrency(d.value)}</p>
    </div>
  );
};

// ── Types ─────────────────────────────────────────────────────────────────
interface PendingTransaction {
  id: string;
  property_number: string;
  seller: string;
  buyer: string;
  expected_acte_date: string;
  compromise_date: string;
  fees_without_tax: number;
  net_fees_without_tax: number;
  agencies: string[];
  company: string;
  retrocessions: { type: string; amount: number }[];
}

interface CompanyStats { name: string; count: number; total_fees: number; net_fees: number }

type SortField = 'seller_buyer' | 'company' | 'compromise_date' | 'expected_acte_date' | 'net_fees_without_tax';
type SortDirection = 'asc' | 'desc' | null;

const getMonthNumber = (monthName: string) => {
  const months: Record<string, number> = {
    'janvier': 0, 'février': 1, 'mars': 2, 'avril': 3, 'mai': 4, 'juin': 5,
    'juillet': 6, 'août': 7, 'septembre': 8, 'octobre': 9, 'novembre': 10, 'décembre': 11
  };
  return months[monthName.toLowerCase()] ?? 0;
};

// ── Composant principal ───────────────────────────────────────────────────
export default function PendingTransactions() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [transactions, setTransactions] = useState<PendingTransaction[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<string>('all');
  const [fiscalYear, setFiscalYear] = useState<number>(() => {
    const today = new Date();
    return today.getMonth() + 1 < 4 ? today.getFullYear() - 1 : today.getFullYear();
  });
  const [period, setPeriod] = useState<'year' | 'quarter' | 'month'>('year');
  const [selectedPeriod, setSelectedPeriod] = useState('1');
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [showAllYears, setShowAllYears] = useState(true);
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  useEffect(() => { loadData(); }, [fiscalYear, period, selectedPeriod, showAllYears]);

  const loadData = async () => {
    try {
      setLoading(true);
      let startDate: string;
      let endDate: string;

      if (showAllYears) {
        startDate = '2000-01-01';
        endDate = '2100-12-31';
      } else {
        if (period === 'year') {
          startDate = `${fiscalYear}-04-01`;
          endDate = `${fiscalYear + 1}-03-31`;
        } else if (period === 'quarter') {
          const quarterMap = {
            '1': { start: '04', end: '06' }, '2': { start: '07', end: '09' },
            '3': { start: '10', end: '12' }, '4': { start: '01', end: '03' }
          };
          const quarter = quarterMap[selectedPeriod as keyof typeof quarterMap];
          const year = selectedPeriod === '4' ? fiscalYear + 1 : fiscalYear;
          startDate = `${year}-${quarter.start}-01`;
          endDate = `${year}-${quarter.end}-${new Date(year, parseInt(quarter.end), 0).getDate()}`;
        } else {
          ({ startDate, endDate } = getMonthRangeDates(selectedPeriod, fiscalYear));
        }
      }

      let query = supabase
        .from('transactions')
        .select(`
          id, property_number, seller, buyer, sale_price, fees_with_tax, fees_without_tax,
          mandate_type, property_origin, status, compromise_date, expected_acte_date,
          regul, commentaire_regul,
          transaction_agencies (agency, agencies (company)),
          transaction_negotiators (negotiator:negotiators (id, firstname, lastname), role, percentage),
          transaction_assistants (assistant:assistants (id, firstname, lastname), role),
          transaction_retrocessions (id, type, amount, payment_date)
        `)
        .eq('status', 'compromis');

      if (!showAllYears) {
        query = query.or(dateRangesFilter('compromise_date', getPeriodRanges(period, selectedPeriod, fiscalYear, startDate, endDate)));
      }

      const { data, error: transactionsError } = await query;
      if (transactionsError) throw transactionsError;

      const formatted = data.map(t => {
        const totalRetrocessions = t.transaction_retrocessions?.reduce((sum: number, r: any) => sum + r.amount, 0) || 0;
        return {
          ...t,
          agencies: t.transaction_agencies.map((ta: any) => ta.agency),
          company: t.transaction_agencies[0]?.agencies?.company || 'Unknown',
          negotiators: t.transaction_negotiators?.map((tn: any) => ({
            id: tn.negotiator.id,
            name: `${tn.negotiator.firstname} ${tn.negotiator.lastname}`,
            role: tn.role, percentage: tn.percentage
          })) || [],
          assistants: t.transaction_assistants?.map((ta: any) => ({
            id: ta.assistant.id,
            name: `${ta.assistant.firstname} ${ta.assistant.lastname}`,
            role: ta.role
          })) || [],
          retrocessions: t.transaction_retrocessions?.map((tr: any) => ({
            id: tr.id, type: tr.type, amount: tr.amount, payment_date: tr.payment_date
          })) || [],
          net_fees_without_tax: t.fees_without_tax - totalRetrocessions
        };
      });

      setTransactions(formatted);
    } catch (err: any) {
      console.error('Error loading transactions:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortDirection === 'asc') setSortDirection('desc');
      else { setSortDirection(null); setSortField(null); }
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ArrowUpDown size={13} className="text-slate-400" />;
    return sortDirection === 'asc'
      ? <ArrowUp size={13} className="text-indigo-500" />
      : <ArrowDown size={13} className="text-indigo-500" />;
  };

  const filteredTransactions = useMemo(() => {
    let f = transactions;
    if (selectedCompany !== 'all') f = f.filter(t => t.company === selectedCompany);
    if (selectedMonth) f = f.filter(t => {
      const my = new Date(t.expected_acte_date).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
      return my === selectedMonth;
    });
    if (sortField && sortDirection) {
      f = [...f].sort((a, b) => {
        let av: any, bv: any;
        switch (sortField) {
          case 'seller_buyer': av = `${a.seller} / ${a.buyer}`.toLowerCase(); bv = `${b.seller} / ${b.buyer}`.toLowerCase(); break;
          case 'company': av = a.company; bv = b.company; break;
          case 'compromise_date': av = a.compromise_date ? new Date(a.compromise_date).getTime() : 0; bv = b.compromise_date ? new Date(b.compromise_date).getTime() : 0; break;
          case 'expected_acte_date': av = a.expected_acte_date ? new Date(a.expected_acte_date).getTime() : 0; bv = b.expected_acte_date ? new Date(b.expected_acte_date).getTime() : 0; break;
          case 'net_fees_without_tax': av = a.net_fees_without_tax; bv = b.net_fees_without_tax; break;
          default: return 0;
        }
        if (av < bv) return sortDirection === 'asc' ? -1 : 1;
        if (av > bv) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return f;
  }, [transactions, selectedCompany, selectedMonth, sortField, sortDirection]);

  const companyStats = useMemo<CompanyStats[]>(() => {
    const stats = transactions.reduce((acc, t) => {
      if (!acc[t.company]) acc[t.company] = { name: t.company, count: 0, total_fees: 0, net_fees: 0 };
      acc[t.company].count++;
      acc[t.company].total_fees += t.fees_without_tax;
      acc[t.company].net_fees += t.net_fees_without_tax;
      return acc;
    }, {} as Record<string, CompanyStats>);
    return Object.values(stats);
  }, [transactions]);

  const monthlyStats = useMemo(() => {
    const stats = filteredTransactions.reduce((acc, t) => {
      if (!t.expected_acte_date) return acc;
      const my = new Date(t.expected_acte_date).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
      if (!acc[my]) acc[my] = { name: my, count: 0, total_fees: 0, net_fees: 0 };
      acc[my].count++;
      acc[my].total_fees += t.fees_without_tax;
      acc[my].net_fees += t.net_fees_without_tax;
      return acc;
    }, {} as Record<string, { name: string; count: number; total_fees: number; net_fees: number }>);
    return Object.values(stats).sort((a, b) => {
      const [mA, yA] = a.name.split(' ');
      const [mB, yB] = b.name.split(' ');
      return new Date(yA, getMonthNumber(mA)).getTime() - new Date(yB, getMonthNumber(mB)).getTime();
    });
  }, [filteredTransactions]);

  const totals = useMemo(() => filteredTransactions.reduce(
    (acc, t) => ({ count: acc.count + 1, total_fees: acc.total_fees + t.fees_without_tax, net_fees: acc.net_fees + t.net_fees_without_tax }),
    { count: 0, total_fees: 0, net_fees: 0 }
  ), [filteredTransactions]);

  // ── Loading / Error states ──────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3 text-slate-400">
        <Loader2 size={28} className="animate-spin text-indigo-500" />
        <span className="text-sm">Chargement…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-700 text-sm">
        <AlertCircle size={16} className="shrink-0" />
        {error}
      </div>
    );
  }

  const thCls = 'px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:text-slate-700 transition-colors select-none';
  const tdCls = 'px-4 py-3.5 text-sm whitespace-nowrap';
  const fmtDate = (d?: string) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="relative rounded-2xl overflow-hidden bg-gradient-to-r from-slate-800 via-slate-900 to-indigo-950 px-7 py-6 shadow-lg">
        <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full -translate-y-1/2 translate-x-1/3 pointer-events-none" />
        <div className="absolute bottom-0 right-32 w-40 h-40 bg-violet-500/10 rounded-full translate-y-1/2 pointer-events-none" />

        <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center shrink-0">
              <Clock size={22} className="text-white/80" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white tracking-tight">Transactions en cours</h1>
              <p className="text-sm text-slate-400 mt-0.5">
                <span className="text-indigo-300 font-semibold">{filteredTransactions.length}</span>
                {' '}compromis affiché{filteredTransactions.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <div className={showAllYears ? 'opacity-40 pointer-events-none' : ''}>
            <FiscalYearSelector
              value={fiscalYear}
              period={period}
              selectedPeriod={selectedPeriod}
              onChange={(year, newPeriod, newSelectedPeriod) => {
                setFiscalYear(year); setPeriod(newPeriod); setSelectedPeriod(newSelectedPeriod);
              }}
              variant="dark"
            />
          </div>
        </div>
      </div>

      {/* ── Filters card ── */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="px-5 py-3.5 border-b border-slate-100 bg-slate-50 flex items-center gap-2.5 rounded-t-2xl">
          <div className="w-7 h-7 rounded-lg bg-indigo-100 flex items-center justify-center">
            <SlidersHorizontal size={14} className="text-indigo-600" />
          </div>
          <h2 className="text-xs font-bold uppercase tracking-wider text-slate-600">Filtres</h2>
        </div>
        <div className="px-5 py-4 flex flex-wrap items-center gap-4">
          {/* Toggle "Toutes les années" */}
          <label className="flex items-center gap-2.5 cursor-pointer select-none">
            <button
              type="button"
              onClick={() => setShowAllYears(!showAllYears)}
              className={`relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors ${showAllYears ? 'bg-indigo-600' : 'bg-slate-200'}`}
            >
              <span className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${showAllYears ? 'translate-x-4' : 'translate-x-0'}`} />
            </button>
            <span className="text-sm font-medium text-slate-700">Toutes les années</span>
          </label>

          {/* Société */}
          <select
            value={selectedCompany}
            onChange={(e) => setSelectedCompany(e.target.value)}
            className="px-3 py-2 text-sm border border-slate-200 rounded-xl text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors cursor-pointer"
          >
            <option value="all">Toutes les sociétés</option>
            <option value="KI">Kermarrec Immobilier</option>
            <option value="KA">Kermarrec Atlantique</option>
            <option value="DRYADE">Dryade</option>
          </select>

          {/* Active filter pills */}
          {selectedCompany !== 'all' && (
            <span className="inline-flex items-center gap-1.5 bg-indigo-100 text-indigo-700 text-xs font-semibold px-3 py-1.5 rounded-full">
              {selectedCompany}
              <button onClick={() => setSelectedCompany('all')} className="hover:text-indigo-900 transition-colors">
                <X size={12} />
              </button>
            </span>
          )}
          {selectedMonth && (
            <span className="inline-flex items-center gap-1.5 bg-emerald-100 text-emerald-700 text-xs font-semibold px-3 py-1.5 rounded-full">
              {selectedMonth}
              <button onClick={() => setSelectedMonth(null)} className="hover:text-emerald-900 transition-colors">
                <X size={12} />
              </button>
            </span>
          )}
        </div>
      </div>

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Transactions en cours */}
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5">
          <div className="flex items-start justify-between mb-3">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Compromis en cours</p>
            <div className="w-9 h-9 rounded-xl bg-indigo-100 flex items-center justify-center shrink-0">
              <Clock size={16} className="text-indigo-600" />
            </div>
          </div>
          <p className="text-3xl font-bold text-slate-900">{totals.count}</p>
          <div className="mt-3 h-0.5 rounded-full bg-indigo-100">
            <div className="h-full w-2/3 rounded-full bg-indigo-400 opacity-60" />
          </div>
        </div>

        {/* Honoraires HT */}
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5">
          <div className="flex items-start justify-between mb-3">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Honoraires HT prévus</p>
            <div className="w-9 h-9 rounded-xl bg-blue-100 flex items-center justify-center shrink-0">
              <Euro size={16} className="text-blue-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-slate-900 leading-tight">{formatCurrency(totals.total_fees)}</p>
          <div className="mt-3 h-0.5 rounded-full bg-blue-100">
            <div className="h-full w-2/3 rounded-full bg-blue-400 opacity-60" />
          </div>
        </div>

        {/* Net HT */}
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5">
          <div className="flex items-start justify-between mb-3">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Honoraires Net HT prévus</p>
            <div className="w-9 h-9 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
              <TrendingUp size={16} className="text-emerald-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-slate-900 leading-tight">{formatCurrency(totals.net_fees)}</p>
          <div className="mt-3 h-0.5 rounded-full bg-emerald-100">
            <div className="h-full w-2/3 rounded-full bg-emerald-400 opacity-60" />
          </div>
        </div>
      </div>

      {/* ── Charts ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Donut – Répartition par société */}
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6">
          <div className="flex items-center gap-2.5 mb-5">
            <div className="w-7 h-7 rounded-lg bg-indigo-100 flex items-center justify-center">
              <Building2 size={14} className="text-indigo-600" />
            </div>
            <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide">Répartition par société</h3>
          </div>

          {companyStats.length === 0 ? (
            <div className="h-[260px] flex items-center justify-center text-slate-400 text-sm">Aucune donnée</div>
          ) : (
            <>
              <div className="h-[220px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={companyStats}
                      dataKey="net_fees"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={58}
                      outerRadius={90}
                      paddingAngle={3}
                      onClick={(data) => setSelectedCompany(data.name)}
                      cursor="pointer"
                      strokeWidth={0}
                    >
                      {companyStats.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={getColor(entry.name, index)}
                          opacity={selectedCompany === 'all' || selectedCompany === entry.name ? 1 : 0.35}
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<PieTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              {/* Custom legend */}
              <div className="mt-3 space-y-2">
                {companyStats.map((entry, idx) => (
                  <div
                    key={entry.name}
                    className="flex items-center justify-between cursor-pointer group"
                    onClick={() => setSelectedCompany(selectedCompany === entry.name ? 'all' : entry.name)}
                  >
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: getColor(entry.name, idx) }} />
                      <span className={`text-sm font-medium transition-colors ${selectedCompany === entry.name ? 'text-slate-900' : 'text-slate-600 group-hover:text-slate-800'}`}>
                        {entry.name}
                      </span>
                      <span className="text-xs text-slate-400">({entry.count} compromis)</span>
                    </div>
                    <span className="text-sm font-semibold text-slate-700">{formatCurrency(entry.net_fees)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Bar – Prévisions mensuelles */}
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-emerald-100 flex items-center justify-center">
                <TrendingUp size={14} className="text-emerald-600" />
              </div>
              <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide">Prévisions mensuelles</h3>
            </div>
          </div>

          {monthlyStats.length === 0 ? (
            <div className="h-[280px] flex items-center justify-center text-slate-400 text-sm">Aucune donnée</div>
          ) : (
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthlyStats} barCategoryGap="30%">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11, fill: '#94a3b8' }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => {
                      const parts = v.split(' ');
                      return parts[0].substring(0, 3).charAt(0).toUpperCase() + parts[0].substring(0, 3).slice(1) + ' ' + parts[1]?.slice(-2);
                    }}
                  />
                  <YAxis
                    tickFormatter={(v) => {
                      if (v >= 1000000) return `${(v / 1000000).toFixed(1)}M€`;
                      if (v >= 1000) return `${(v / 1000).toFixed(0)}k€`;
                      return `${v}€`;
                    }}
                    tick={{ fontSize: 11, fill: '#94a3b8' }}
                    axisLine={false}
                    tickLine={false}
                    width={60}
                  />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f8fafc' }} />
                  <Bar
                    name="Honoraires Net HT"
                    dataKey="net_fees"
                    fill="#10b981"
                    radius={[6, 6, 0, 0]}
                    onClick={(data) => setSelectedMonth(selectedMonth === data.name ? null : data.name)}
                    cursor="pointer"
                  >
                    {monthlyStats.map((entry, index) => (
                      <Cell
                        key={`bar-${index}`}
                        fill={selectedMonth === null || selectedMonth === entry.name ? '#10b981' : '#d1fae5'}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          <p className="mt-2 text-xs text-slate-400 text-center">Date prévisionnelle de l'acte · Cliquez sur une barre pour filtrer</p>
        </div>
      </div>

      {/* ── Transaction table ── */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="px-5 py-3.5 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-600">
            Liste des transactions ({filteredTransactions.length})
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px]">
            <thead>
              <tr className="border-b border-slate-100">
                <th className={thCls} onClick={() => handleSort('seller_buyer')}>
                  <div className="flex items-center gap-1">Vendeur / Acquéreur <SortIcon field="seller_buyer" /></div>
                </th>
                <th className={thCls} onClick={() => handleSort('company')}>
                  <div className="flex items-center gap-1">Société <SortIcon field="company" /></div>
                </th>
                <th className={thCls} onClick={() => handleSort('compromise_date')}>
                  <div className="flex items-center gap-1">Date compromis <SortIcon field="compromise_date" /></div>
                </th>
                <th className={thCls} onClick={() => handleSort('expected_acte_date')}>
                  <div className="flex items-center gap-1">Date prév. acte <SortIcon field="expected_acte_date" /></div>
                </th>
                <th className={thCls} onClick={() => handleSort('net_fees_without_tax')}>
                  <div className="flex items-center gap-1">Hono. Net HT <SortIcon field="net_fees_without_tax" /></div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredTransactions.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-sm text-slate-400">Aucune transaction trouvée</td>
                </tr>
              ) : (
                filteredTransactions.map((transaction) => {
                  const companyColor = COMPANY_COLOR[transaction.company] ?? '#6366f1';
                  const companyBadgeCls =
                    transaction.company === 'KI' ? 'bg-indigo-50 text-indigo-700' :
                    transaction.company === 'KA' ? 'bg-emerald-50 text-emerald-700' :
                    'bg-violet-50 text-violet-700';

                  return (
                    <tr
                      key={transaction.id}
                      className="cursor-pointer hover:bg-slate-50/70 transition-colors"
                      onClick={() => handleTransactionClick(transaction as Transaction)}
                    >
                      <td className={tdCls}>
                        <div className="flex flex-col gap-0.5">
                          <span className="font-semibold text-slate-800">{transaction.seller || '—'}</span>
                          <span className="text-xs text-slate-400">/ {transaction.buyer || '—'}</span>
                        </div>
                      </td>
                      <td className={tdCls}>
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${companyBadgeCls}`}>
                          <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: companyColor }} />
                          {transaction.company}
                        </span>
                      </td>
                      <td className={tdCls + ' text-slate-500'}>{fmtDate(transaction.compromise_date)}</td>
                      <td className={tdCls}>
                        {transaction.expected_acte_date ? (
                          <span className={`inline-flex items-center gap-1.5 text-sm ${
                            new Date(transaction.expected_acte_date) < new Date()
                              ? 'text-rose-600 font-medium'
                              : 'text-slate-600'
                          }`}>
                            {fmtDate(transaction.expected_acte_date)}
                            {new Date(transaction.expected_acte_date) < new Date() && (
                              <span className="text-xs bg-rose-100 text-rose-600 px-1.5 py-0.5 rounded-full font-semibold">En retard</span>
                            )}
                          </span>
                        ) : '—'}
                      </td>
                      <td className={tdCls + ' font-bold text-emerald-700'}>{formatCurrency(transaction.net_fees_without_tax)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      <ViewTransactionModal
        transaction={selectedTransaction}
        isOpen={isViewModalOpen}
        onClose={() => { setIsViewModalOpen(false); setSelectedTransaction(null); }}
      />
    </div>
  );
}
